/* eslint-disable @typescript-eslint/camelcase */
import Permission from 'components/ValidPermission';
import { Grid, makeStyles, Box, Typography, Checkbox } from '@material-ui/core';
import { ChevronLeft } from '@material-ui/icons';
import TableItems, {
  MenuActionItem,
  MenuActions,
  StatusIcon,
  TableCell,
  TableRow,
} from 'components/TableItems';
import ButtonGroup from 'components/ButtonGroup';
import Button from 'components/Button';
import PageTitle from 'components/PageTitle';
import i18n from 'i18n';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import {
  ListAllUsersDataProps,
  GetAllUsersFields,
} from 'modules/users/interfaces';
import { useTableFilters, Query } from 'hooks/useTableFilters';
import {
  getUsersToManageRequest,
  removeUsersFromClientsRequest,
  removeUserFromAllClientsRequest,
} from 'modules/users/actions';
import palette from 'theme/palette';
import { useCheckboxes } from 'hooks';
import { formatDateValue } from 'utils/dateFormat';
import { history } from 'store';
import { PermissionsData } from 'modules/authenticate/interfaces';
import { modalOpen, modalClose } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import { removeClientUserRequest } from 'modules/clients/actions';
import SearchFilterBar from 'components/SearchFilterBar';
import { getHeaders } from './headers';
import { filterOptions } from './filterOptions';
import styles from './styles';
import { parseNameLdap } from 'utils/helpers';

const useStyles = makeStyles(styles);

const MAX_COUNT_FOR_REMOVAL = 500;

const createCheckItem = (item: GetAllUsersFields) => ({
  user_uuid: item?.user_uuid,
  client_uuid: item?.client?.client_uuid,
});

interface UsersManagementProps {
  isLoading: boolean;
  permissions?: PermissionsData;
  usersToManage: ListAllUsersDataProps;
  removeUserFromAllClientsRequest: ({ userUuid, getUsersFn }) => void;
  getUsersToManageRequest: (props) => void;
  modalClose: () => void;
  modalOpen: (props: ModalFields) => void;
  removeClientUserRequest: (props: {
    clientUuid: string;
    userUuid: string;
    query?: Query;
    getUsersFn: () => void;
  }) => void;
  removeUsersFromClientsRequest: ({ usersToRemove, getUsersFn }) => void;
}

const UsersManagement = ({
  isLoading,
  permissions,
  usersToManage,
  removeUserFromAllClientsRequest,
  getUsersToManageRequest,
  modalClose,
  modalOpen,
  removeClientUserRequest,
  removeUsersFromClientsRequest,
}: UsersManagementProps) => {
  const classes = useStyles();
  const {
    itemOrder,
    searchValue,
    query,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    setSearchValue,
    setQuery,
  } = useTableFilters({
    offset: 0,
    limit: 25,
    search: '',
    filters: {
      last_access: '90d',
      created_at: '90d',
      is_integration_user: 'false',
    },
  });
  const {
    checks,
    handleCheck,
    handleAllCheck,
    isChecked,
    clearChecks,
  } = useCheckboxes<
    GetAllUsersFields,
    { user_uuid: string; client_uuid: string }
  >({
    items: usersToManage?.results,
    compareChecks: (a, b) =>
      a.user_uuid === b.user_uuid && a.client_uuid === b.client_uuid,
    isItemChecked: (item, check) =>
      item?.user_uuid === check.user_uuid &&
      item?.client?.client_uuid === check.client_uuid,
    createCheckItem,
  });

  const [refresh, setRefresh] = React.useState(false);
  const [showTable, setShowTable] = React.useState(false);

  const isAllChecked =
    !!usersToManage?.results?.length &&
    usersToManage.results.every(item => isChecked(createCheckItem(item)));
  const exceededRemovalLimit = checks.length > MAX_COUNT_FOR_REMOVAL;

  const getUsersToManage = React.useCallback(
    (refresh = false) => {
      if (!showTable) return;
      getUsersToManageRequest({
        ...query,
      });

      setRefresh(refresh);
    },
    [getUsersToManageRequest, query, showTable]
  );

  React.useEffect(() => {
    const interval = setInterval(() => {
      if (usersToManage.results.some(user => user.is_removing)) {
        getUsersToManage(true);
      } else {
        clearInterval(interval);
      }
    }, 5000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersToManage]);

  const handleRemoveUsersFromClients = () => {
    modalOpen({
      title: i18n.t('USERS_PAGE.DELETE_MULTIPLE_USERS_MODAL_TITLE_KEY'),
      content: i18n.t('USERS_PAGE.DELETE_MULTIPLE_USERS_MODAL_CONTENT_KEY', {
        count: checks.length,
      }),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              removeUsersFromClientsRequest({
                usersToRemove: checks,
                getUsersFn: () => getUsersToManage(),
              });
              modalClose();
              clearChecks();
            }}
          >
            {i18n.t('CONFIRM_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const handleRemoveUserFromClient = (item: GetAllUsersFields) => {
    modalOpen({
      title: i18n.t('WANT_REMOVE_USER_KEY', {
        name: `${item?.first_name} ${item?.last_name}`,
      }),
      content: i18n.t('REMOVE_USER_DESCRIPTION_KEY'),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              removeClientUserRequest({
                clientUuid: item?.client.client_uuid,
                userUuid: item?.user_uuid,
                query,
                getUsersFn: () => getUsersToManage(),
              });
              modalClose();
            }}
          >
            {i18n.t('REMOVE_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const handleRemoveUserFromAllClients = (item: GetAllUsersFields) => {
    modalOpen({
      title: i18n.t('USERS_PAGE.DELETE_ALL_MODAL_TITLE_KEY'),
      content: i18n.t('USERS_PAGE.DELETE_ALL_MODAL_CONTENT_KEY'),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              removeUserFromAllClientsRequest({
                userUuid: item?.user_uuid,
                getUsersFn: () => getUsersToManage(),
              });
              modalClose();
            }}
          >
            {i18n.t('CONFIRM_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  useEffect(() => {
    getUsersToManage();
  }, [getUsersToManage]);

  return (
    <Grid container>
      <Grid
        container
        direction="row"
        alignItems="center"
        justify="space-between"
      >
        <PageTitle
          title={i18n.t('USERS_PAGE.USERS_MANAGEMENT_TITLE_KEY')}
          subtitle={i18n.t('USERS_PAGE.USERS_MANAGEMENT_SUBTITLE_KEY')}
        />

        <Grid item xs={3} className={classes.actionsButtonsGrid}>
          <Button
            startIcon={<ChevronLeft className={classes.backIcon} />}
            onClick={() => history.push('/all-users')}
            variant="outlined"
            id="back-button"
          >
            {i18n.t('BACK_KEY')}
          </Button>
          <Permission
            value={['users_recovery', 'delete_user', 'delete_user_permanently']}
          >
            <ButtonGroup
              label={i18n.t('GENERAL_ACTIONS_KEY')}
              options={[
                {
                  label: i18n.t('USERS_PAGE.DELETED_USERS_TITLE_KEY'),
                  onClick: () => history.push('/all-users/recovery'),
                  hidden:
                    !permissions?.users_recovery &&
                    !permissions?.delete_user_permanently,
                },
                {
                  label: i18n.t('USERS_PAGE.DELETE_MULTIPLE_USERS_ACTION_KEY'),
                  onClick: () => handleRemoveUsersFromClients(),
                  disabled: exceededRemovalLimit || !checks.length,
                  hidden: !permissions?.delete_user,
                },
              ]}
            />
          </Permission>
        </Grid>

        <Grid container>
          <SearchFilterBar
            filterOptions={filterOptions}
            isLoading={isLoading}
            searchValue={searchValue}
            query={query}
            onApplyFilters={() => setShowTable(true)}
            setSearchValue={setSearchValue}
            setQuery={setQuery}
          />

          {showTable && (
            <>
              <Grid item className={classes.selectedUsersCounter}>
                <Typography>
                  {`${i18n.t('USERS_PAGE.SELECTED_USERS_KEY')}: ${
                    checks.length
                  } / `}
                  <Box
                    color={exceededRemovalLimit ? palette.red : palette.green}
                    fontSize={12}
                    fontWeight="bold"
                    display="inline"
                  >
                    {MAX_COUNT_FOR_REMOVAL}
                  </Box>
                </Typography>
              </Grid>

              <TableItems
                header={
                  <>
                    <TableCell className={classes.checkboxCell}>
                      <Checkbox
                        id="select-all-users"
                        checked={isAllChecked}
                        onClick={() => handleAllCheck(isAllChecked)}
                      />
                    </TableCell>
                    {getHeaders(permissions).map(item => (
                      <TableCell
                        centerTitle={item?.centerTitle}
                        key={item?.key}
                        order={item?.order}
                        markOderItem={itemOrder === item?.key}
                        handleOrder={() =>
                          item?.order ? handleOrder(item?.key) : null
                        }
                      >
                        {i18n.t(item?.i18n)}
                      </TableCell>
                    ))}
                  </>
                }
                renderItems={(item: GetAllUsersFields) => (
                  <TableRow
                    key={`${item.user_uuid}-${item.client?.client_uuid}`}
                  >
                    <TableCell className={classes.checkboxCell}>
                      <Checkbox
                        id="select-user"
                        onClick={() => handleCheck(createCheckItem(item))}
                        checked={isChecked(createCheckItem(item))}
                      />
                    </TableCell>
                    <TableCell
                      className={classes.centerFields}
                      tooltip={`${item?.first_name} ${item?.last_name}`}
                    >
                      {`${item?.first_name} ${item?.last_name}`}
                    </TableCell>
                    <TableCell
                      className={classes.emailField}
                      ellipsis
                      tooltip={item?.email}
                    >
                      {item?.email}
                    </TableCell>
                    <TableCell
                      className={classes.centerFields}
                      tooltip={item?.username}
                      ellipsis
                    >
                      {item?.username}
                    </TableCell>
                    <TableCell
                      className={classes.centerFields}
                      tooltip={item?.client?.client_name}
                      ellipsis
                    >
                      {item?.client?.client_name}
                    </TableCell>
                    <TableCell
                      className={classes.centerLongFields}
                      tooltip={item?.environment?.environment_name}
                      ellipsis
                    >
                      {item?.environment?.environment_name}
                    </TableCell>
                    <TableCell
                      className={classes.centerLongFields}
                      tooltip={item?.partner?.partner_name}
                      ellipsis
                    >
                      {item?.partner?.partner_name}
                    </TableCell>
                    <TableCell
                      className={classes.centerLongFields}
                      tooltip={parseNameLdap(item?.partner?.ldap_type)}
                      ellipsis
                    >
                      {parseNameLdap(item?.partner?.ldap_type)}
                    </TableCell>
                    <TableCell
                      className={classes.centerSmallFields}
                      tooltip={
                        item?.is_removing
                          ? i18n.t('REMOVING_KEY')
                          : i18n.t(item?.active ? 'ACTIVE_KEY' : 'INACTIVE_KEY')
                      }
                    >
                      <Grid container justify="center">
                        {item?.is_removing ? (
                          <StatusIcon status="loading" />
                        ) : (
                          <StatusIcon
                            status={item?.active ? 'success' : 'inactive'}
                          />
                        )}
                      </Grid>
                    </TableCell>
                    <TableCell centerTitle className={classes.centerFields}>
                      {formatDateValue(item?.created_at)}
                    </TableCell>
                    <TableCell centerTitle className={classes.centerFields}>
                      {formatDateValue(item?.last_access)}
                    </TableCell>
                    <Permission value="delete_user">
                      <TableCell textAlignCenter>
                        <MenuActions>
                          <MenuActionItem
                            id="btn-remove-user-from-client"
                            onClick={() => handleRemoveUserFromClient(item)}
                          >
                            {i18n.t('USERS_PAGE.DELETE_BUTTON_KEY')}
                          </MenuActionItem>
                          <MenuActionItem
                            id="btn-remove-user-from-all-clients"
                            onClick={() => handleRemoveUserFromAllClients(item)}
                          >
                            {i18n.t('USERS_PAGE.DELETE_ALL_BUTTON_KEY')}
                          </MenuActionItem>
                        </MenuActions>
                      </TableCell>
                    </Permission>
                  </TableRow>
                )}
                data={usersToManage}
                isLoading={!refresh && isLoading}
                onChangePage={handlePageChange}
                onChangeLimit={handleLimitChange}
                notFoundText={i18n.t('USERS_PAGE.NO_USERS_KEY')}
              />
            </>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { data },
  clients: { isLoading: isLoadingClients },
  users: { isLoading, isLoadingUsersToManage, usersToManage },
}: ApplicationState) => ({
  isLoading: isLoadingUsersToManage || isLoading || isLoadingClients,
  permissions: data?.permissions,
  usersToManage,
});

const mapDispatchToProps = {
  getUsersToManageRequest,
  modalClose,
  modalOpen,
  removeClientUserRequest,
  removeUserFromAllClientsRequest,
  removeUsersFromClientsRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(UsersManagement);
